import { FunctionComponent, useState } from 'react';
import { BodyText, Button, Div, Flex, Gutter, Input } from '@gaiads/telia-react-component-library';
import { modalClose, modalShow } from '../../store/actions';
import PhoneNumberVerified from './PhoneNumberVerified';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { devLogger, verifyPhoneNumberPasscode } from '../../utils';
import { VERIFY_PASSCODE } from '../../utils/constants';
import { User } from '../../types';

interface EnterPasscodeProps {
  phoneNumber: string;
  verificationId: string;
  openVerifyPhoneNumberFlow: () => void;
}

const EnterPasscode: FunctionComponent<EnterPasscodeProps> = ({ phoneNumber, verificationId, openVerifyPhoneNumberFlow }) => {
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState(false);
  const [nextButtonSpinnerLoading, setNextButtonSpinnerLoading] = useState(false);
  const [isPasscodeValid, setIsPasscodeValid] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation('phoneNumberVerification');

  const handleVerifyPasscode = async () => {
    setNextButtonSpinnerLoading(true);
    devLogger('Sending request to verify passcode');
    const user: User | null = await verifyPhoneNumberPasscode(passcode, verificationId);
    if (user) {
      dispatch(
        modalShow(
          t('PhoneNumberVerified.title'),
          t('PhoneNumberVerified.exitButton'),
          '',
          () => dispatch(modalClose()),
          () => dispatch(modalClose()),
          <PhoneNumberVerified />,
        ),
      );
    } else {
      setPasscodeError(true);
      setNextButtonSpinnerLoading(false);
    }
  };

  const goBackToRequestPasscode = () => {
    dispatch(modalClose());
    openVerifyPhoneNumberFlow();
  };

  return (
    <Div>
      <BodyText size={'md'}>
        {t('EnterPasscode.passcodeSentPart1')} <b>{phoneNumber}</b> {t('EnterPasscode.passcodeSentPart2')}
      </BodyText>
      <Gutter />

      <Flex spaceBetween>
        <BodyText size={'sm'}>{t('EnterPasscode.passcode')}</BodyText>
      </Flex>

      <Gutter size={'xs'} />
      <Input
        data-testid="passcode-input"
        showCustomLabel={true}
        value={passcode}
        label={''}
        type={'password'}
        errorText={passcodeError ? t('EnterPasscode.wrongPasscodeError') : ''}
        helperText={t('EnterPasscode.helperText')}
        onChange={e => {
          setPasscode(e.target.value);
          setIsPasscodeValid(!!e.target.value.match(VERIFY_PASSCODE));
        }}
      />

      <Gutter size={'xs'} />

      {passcodeError && (
        <BodyText size={'sm'} color={'gray500'}>
          {t('EnterPasscode.wrongPhoneNumber')}
        </BodyText>
      )}

      <Gutter />
      <Flex alignRight>
        <Button
          loading={nextButtonSpinnerLoading}
          size={'sm'}
          onClick={passcodeError ? goBackToRequestPasscode : handleVerifyPasscode}
          variant={'primary'}
          disabled={!isPasscodeValid}
        >
          {passcodeError ? t('EnterPasscode.backToPasscodeButton') : t('EnterPasscode.verifyButton')}
        </Button>
      </Flex>
    </Div>
  );
};

export default EnterPasscode;
